// Sanitize an error before passing it to console.error in patient-facing
// code paths. Sentry's `captureConsoleIntegration({ levels: "error" })`
// captures any object passed to console.error — for axios errors that
// includes `err.config.data` (the full request body) and
// `err.response.data` (the full response body). Patient request bodies
// carry shipping address / order line items / clinical-profile fields
// and are HIPAA-relevant.
//
// `src/index.js` has a global Sentry beforeSend that strips these fields
// when the URL matches our backend, so this helper is belt-and-suspenders
// — but it also keeps the local devtools console from printing PHI on
// every patient session that hits a network blip.
//
// Returns a flat object that's safe to forward to Sentry. Always
// preserves the diagnostic minimum: status code, status text, request
// method + path, error name + message.

export function sanitizeErrorForLog(err) {
  if (!err) return { error: "unknown" };
  if (typeof err === "string") return { error: err.slice(0, 512) };

  const out = {};
  if (err.name) out.name = err.name;
  if (err.message) out.message = String(err.message).slice(0, 512);
  // Axios attaches `code` ("ECONNABORTED", etc.) and a `config` block.
  if (err.code) out.code = err.code;
  if (err.config) {
    if (err.config.method) out.method = err.config.method;
    // Strip query string just in case it carries identifiers.
    if (err.config.url) {
      try {
        const u = new URL(err.config.url, "http://placeholder");
        out.url = u.pathname;
      } catch {
        out.url = String(err.config.url).split("?")[0];
      }
    }
  }
  if (err.response) {
    if (typeof err.response.status === "number") out.status = err.response.status;
    if (err.response.statusText) out.statusText = err.response.statusText;
  }
  return out;
}
